import React, { useEffect, useMemo, useState } from "react";

import { fetchStudentHistoryAPI, hideStudentReviewAPI } from "../levelTestAPI";
import "./StudentLevelTestHistory.css";

// 학생별 학습 내역의 '레벨테스트' 유형. 응시 한 건이 한 줄이고, 그 줄에 영역별 점수와
// 복습 진행도를 함께 보여준다. 우리 과제와 계통이 달라 표를 따로 그린다.
//
// 이름·클래스 필터는 학습 내역 화면이 이미 걸고 있으므로 여기서는 이름으로만 거른다.
const StudentLevelTestHistory = ({ keyword, studentId }) => {
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const toggleReviewHidden = (row) => {
    const nextHidden = !row.is_review_hidden;
    if (
      nextHidden &&
      !window.confirm(
        `${row.name} 학생의 ${row.month} 복습을 과제 목록에서 감출까요?\n\n` +
          "이미 한 복습 기록과 리포트는 그대로 남습니다."
      )
    )
      return;

    hideStudentReviewAPI(row.session_id, nextHidden)
      .then(() =>
        setResults((previous) =>
          previous.map((one) =>
            one.session_id === row.session_id
              ? { ...one, is_review_hidden: nextHidden }
              : one
          )
        )
      )
      .catch(() => window.alert("바꾸지 못했습니다."));
  };

  useEffect(() => {
    let isCurrent = true;
    fetchStudentHistoryAPI()
      .then((data) => {
        if (isCurrent) {
          setResults(data.results || []);
          setIsLoading(false);
        }
      })
      .catch(() => {
        if (isCurrent) {
          setIsLoading(false);
        }
      });

    return () => {
      isCurrent = false;
    };
  }, []);

  const visibleResults = useMemo(() => {
    let list = results;

    if (keyword && keyword.trim()) {
      const text = keyword.trim();
      list = list.filter((row) => row.name.includes(text));
    }

    // 왼쪽 필터에서 학생을 고르면 그 학생 것만 남긴다.
    if (studentId) {
      list = list.filter((row) => row.student_id === Number(studentId));
    }

    return list;
  }, [results, keyword, studentId]);

  if (isLoading) {
    return <p className="lt-history__empty">불러오는 중…</p>;
  }

  if (!visibleResults.length) {
    return <p className="lt-history__empty">레벨테스트 기록이 없습니다.</p>;
  }

  return (
    <div className="lt-history">
      <table className="lt-history__table">
        <tbody>
          <tr className="lt-history__head">
            <td>학생</td>
            <td>학년</td>
            <td>응시</td>
            <td>레벨</td>
            <td className="is-center">점수</td>
            <td>영역별</td>
            <td className="is-center">복습</td>
            <td className="is-center">리포트</td>
          </tr>
          {visibleResults.map((row) => (
            <tr key={row.session_id}>
              <td>
                <a
                  className="lt-history__student"
                  href={row.student_url}
                  target="_blank"
                  rel="noreferrer"
                >
                  {row.name}
                </a>
              </td>
              <td>{row.grade}학년</td>
              <td className="lt-history__month">{row.month}</td>
              <td className="lt-history__stage">
                S{String(row.stage).padStart(2, "0")} {row.stage_name}
              </td>
              <td className="is-center">
                {row.score_percent === null ? (
                  <span className="lt-history__progress">진행중</span>
                ) : (
                  <b className="lt-history__score">{row.score_percent}%</b>
                )}
              </td>
              <td>
                {row.areas.length === 0 ? (
                  <span className="is-muted">—</span>
                ) : (
                  <span className="lt-history__areas">
                    {row.areas.map((area) => (
                      <span key={area.area} className="lt-history__area">
                        {area.area} <b>{area.pct}%</b>
                      </span>
                    ))}
                  </span>
                )}
              </td>
              <td className="is-center">
                {row.review_done + row.review_todo === 0 ? (
                  <span className="is-muted">—</span>
                ) : (
                  <span
                    className={`lt-history__review${
                      row.review_done === 0 ? " is-none" : ""
                    }`}
                  >
                    {row.review_done} / {row.review_done + row.review_todo}
                  </span>
                )}
                {/* 아이 과제 목록 맨 위에 '기한 지남' 복습이 계속 붙어 있어
                    아이들이 혼란스러워한다는 신고로 넣었다. **기록은 지우지 않고
                    목록에서만 감춘다.** 다시 보이게 할 수도 있다. */}
                {row.review_done + row.review_todo > 0 && (
                  <button
                    type="button"
                    className="lt-history__review-hide"
                    onClick={() => toggleReviewHidden(row)}
                    title={
                      row.is_review_hidden
                        ? "아이 과제 목록에 다시 보이게 합니다"
                        : "아이 과제 목록에서 감춥니다 (기록은 남습니다)"
                    }
                  >
                    {row.is_review_hidden ? "다시 보이기" : "감추기"}
                  </button>
                )}
              </td>
              <td className="is-center">
                {row.report_url ? (
                  <a
                    className="lt-history__report"
                    href={row.report_url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    리포트
                  </a>
                ) : (
                  <span className="is-muted">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default StudentLevelTestHistory;
